import { createSlice } from '@reduxjs/toolkit';
import { Result } from '../../interfaces/changeRequest';
import ActionError from '../../utils/ActionError';
import {
  fetchAllChangeRequests,
  deleteChangeRequests,
  fetchMyChangeRequests,
} from '../ActionTypes/changeRequestsActions';
import { ChangeRequestListingScope } from '../../enums/changeRequest';

interface ChangeRequestGroup {
  key: string;
  label: string;
  changeRequests: Result[];
}

interface ChangeRequestListState {
  records: Result[];
  groupedRecords: ChangeRequestGroup[];
  loading: boolean;
  error: ActionError | string | null;
}

interface ChangeRequestsState {
  allChangeRequests: ChangeRequestListState;
  myChangeRequests: ChangeRequestListState;
  listingScope: ChangeRequestListingScope;
  loading: boolean;
  error: ActionError | string | null;
}

const initialState: ChangeRequestsState = {
  allChangeRequests: {
    records: [],
    groupedRecords: [],
    loading: false,
    error: null,
  },
  myChangeRequests: {
    records: [],
    groupedRecords: [],
    loading: false,
    error: null,
  },
  listingScope: ChangeRequestListingScope.MY,
  loading: false,
  error: null,
};

const changeRequestsItemsSlice = createSlice({
  name: 'changeRequests',
  initialState,
  reducers: {
    resetChangeRequests: () => {
      return initialState;
    },
    setChangeRequestsListingScope: (state, action) => {
      state.listingScope = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch My Change Requests
      .addCase(fetchMyChangeRequests.pending, (state) => {
        state.loading = true;
        state.myChangeRequests.loading = true;
        state.myChangeRequests.error = null;
      })
      .addCase(fetchMyChangeRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.myChangeRequests.loading = false;
        state.myChangeRequests.records = action.payload.data;
        state.myChangeRequests.groupedRecords = action.payload.groupedData;
      })
      .addCase(fetchMyChangeRequests.rejected, (state, action) => {
        state.loading = false;
        state.myChangeRequests.loading = false;
        state.myChangeRequests.error = action.payload as ActionError;
      })

      // Fetch All Change Requests
      .addCase(fetchAllChangeRequests.pending, (state) => {
        state.loading = true;
        state.allChangeRequests.loading = true;
        state.allChangeRequests.error = null;
      })
      .addCase(fetchAllChangeRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.allChangeRequests.loading = false;
        state.allChangeRequests.records = action.payload.data;
        state.allChangeRequests.groupedRecords = action.payload.groupedData;
      })
      .addCase(fetchAllChangeRequests.rejected, (state, action) => {
        state.loading = false;
        state.allChangeRequests.loading = false;
        state.allChangeRequests.error = action.payload as ActionError;
      })

      // Batch delete
      .addCase(deleteChangeRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteChangeRequests.fulfilled, (state, action) => {
        state.loading = false;
        const myCrsPayload = action.payload[ChangeRequestListingScope.MY];
        const allCrsPayload = action.payload[ChangeRequestListingScope.ALL];
        state.myChangeRequests.records = myCrsPayload?.data;
        state.myChangeRequests.groupedRecords = myCrsPayload?.groupedData;
        state.allChangeRequests.records = allCrsPayload?.data;
        state.allChangeRequests.groupedRecords = allCrsPayload?.groupedData;
      })
      .addCase(deleteChangeRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetChangeRequests, setChangeRequestsListingScope } =
  changeRequestsItemsSlice.actions;
export default changeRequestsItemsSlice.reducer;
